import React from 'react';
import SiteNav from '@/components/SiteNav';
import { Link } from 'react-router-dom';
import { Globe } from 'lucide-react';

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{title}</h2>
      <div className="mt-4 space-y-3 text-slate-300 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-300" data-testid="privacy-page">
      <SiteNav />

      <main className="max-w-3xl mx-auto px-6 pt-32 pb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight" style={{ fontFamily: 'Outfit' }}>Privacy Policy</h1>
        <p className="text-sm text-slate-500 mt-3">Last updated: July 2026</p>

        <div className="mt-10 space-y-4 text-lg leading-relaxed">
          <p>
            This policy explains what information EasyPeazy collects, why, and what we do with it.
            We keep it short and honest. In this policy, "we," "us," and "our" mean the operator of
            the EasyPeazy service at easypeazy.site.
          </p>
          <p className="text-base text-slate-400 border-l-2 border-indigo-500/40 pl-5">
            We don't sell your personal information.
          </p>
        </div>

        <Section title="What we collect">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account details</strong> you give us: your name, email address, and — if you provide them — your business name and phone number.</li>
            <li><strong>Domain registration details:</strong> to register a domain, registrars and ICANN require a contact name, email, address, and phone number. We collect these to complete the registration.</li>
            <li><strong>Payment information:</strong> processed by our payment processor. We receive confirmation and limited billing details, but we do not store your full card number.</li>
            <li><strong>Your website content:</strong> the text, images, and settings you add to a site we host for you.</li>
            <li><strong>Basic technical data:</strong> things like your IP address and browser info, used to keep accounts secure and the service working.</li>
          </ul>
        </Section>

        <Section title="How we use it">
          <ul className="list-disc pl-6 space-y-2">
            <li>To create your account and provide the service.</li>
            <li>To register your domain, set up hosting and email, and provision your website.</li>
            <li>To take payment and send you receipts and important service notices.</li>
            <li>To provide support when you ask for it.</li>
            <li>To keep the service secure and to comply with the law.</li>
          </ul>
        </Section>

        <Section title="Who we share it with">
          <p>We share only what's needed to run the service, with providers such as:</p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>A <strong>domain registrar</strong> (and, as required, the public WHOIS / ICANN system) to register and maintain your domain. Domain privacy protection, where offered, helps keep your personal details out of public WHOIS.</li>
            <li>A <strong>hosting provider</strong> to host your website.</li>
            <li>A <strong>payment processor</strong> to handle payments securely.</li>
            <li>An <strong>email delivery provider</strong> to send account and service emails.</li>
            <li>A <strong>content-generation provider</strong> to power the automated design features, when you use them.</li>
          </ul>
          <p className="mt-3">
            We may also disclose information if the law requires it, or to protect the safety,
            rights, or property of our customers or the public. We do not sell your data or share it
            for third-party advertising.
          </p>
        </Section>

        <Section title="Cookies">
          <p>
            We use essential cookies to keep you signed in and to keep your session secure. We don't
            use third-party advertising or cross-site tracking cookies.
          </p>
        </Section>

        <Section title="How long we keep it">
          <p>
            We keep your account and site data for as long as your account is active, and for a
            reasonable period afterward to meet legal, tax, and security obligations. Domain
            registration records are kept as long as required by the registrar and ICANN. When data
            is no longer needed, we delete or anonymize it.
          </p>
        </Section>

        <Section title="Your choices and rights">
          <ul className="list-disc pl-6 space-y-2">
            <li>You can view and update your account details from your portal.</li>
            <li>You can ask us for a copy of your personal information, ask us to correct it, or ask us to delete it.</li>
            <li>You can close your account at any time. Some records may be retained where the law requires.</li>
          </ul>
          <p className="mt-3">To make a request, contact us using the details below.</p>
        </Section>

        <Section title="Security">
          <p>
            We use reasonable technical and organizational measures to protect your information,
            including encrypted connections and access controls. No online service can be perfectly
            secure, so please use a strong, unique password and tell us right away if you notice
            anything suspicious on your account.
          </p>
        </Section>

        <Section title="Children">
          <p>
            EasyPeazy is intended for adults running a website or business. It is not directed to
            children under 18, and we don't knowingly collect personal information from them.
          </p>
        </Section>

        <Section title="Where your data is handled">
          <p>
            EasyPeazy and its providers operate in the United States, so your information may be
            processed there. By using the service, you understand your information may be processed
            in the U.S. and other countries where our providers operate.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            We may update this policy from time to time. When we make a meaningful change, we'll
            update the "Last updated" date and, where appropriate, notify you.
          </p>
        </Section>

        <Section title="Contact us">
          <p>
            Questions about your privacy? Open a support ticket from your EasyPeazy portal, or email
            us at <em>support@easypeazy.site</em>.
          </p>
        </Section>
      </main>

      <footer className="border-t border-slate-800 py-8">
        <div className="max-w-3xl mx-auto px-6 text-sm text-slate-500 flex justify-between">
          <Link to="/" className="inline-flex items-center min-h-[44px] hover:text-white transition-colors">&larr; Home</Link>
          <Link to="/terms" className="inline-flex items-center min-h-[44px] hover:text-white transition-colors">Terms of Service &rarr;</Link>
        </div>
      </footer>
    </div>
  );
}
